import { createContext, useContext, useState, useCallback, useRef, PropsWithChildren, useMemo } from 'react'

import { Geolocation, Position, PositionOptions } from '@capacitor/geolocation'
import { Capacitor } from '@capacitor/core'

import { useToast } from '@/contexts/ToastContext'
import { positionToCoords } from '@/utils/map'
import { Logger } from '@/lib/logger'
import {
  canPromptLocation,
  hasLocationAccess,
  isLocationDenied,
  isLocationDisabledError,
  isPermissionDeniedError,
} from '@/utils/geolocation'

const logger = new Logger('Geolocation')

const PERMANENT_DENIED_MSG = 'Permissão de localização negada. Verifique as configurações do dispositivo.'

const GEOLOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 30000,
}

const LOCATION_RETRY_DELAY_MS = 1200
const RETRY_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 30000,
  maximumAge: 0,
}

interface GeolocationContextValue {
  currentLocation: Position | null
  coords: { lat: number; lng: number } | null
  isPending: boolean
  ensureLocation: () => Promise<Position | null>
  triggerRequestLocation: () => Promise<Position | null>
}

const GeolocationContext = createContext<GeolocationContextValue | undefined>(undefined)

export const GeolocationProvider = ({ children }: PropsWithChildren) => {
  const { show } = useToast()

  const [currentLocation, setCurrentLocation] = useState<Position | null>(null)
  const [isPending, setIsPending] = useState(false)
  const hasNotifiedSilentlyRef = useRef(false)
  const hasSilentlyPromptedRef = useRef(false)

  const emitNotification = useCallback(
    (mode: 'silent' | 'explicit', message: string, severity: 'warning' | 'error') => {
      if (mode !== 'explicit' && hasNotifiedSilentlyRef.current) return
      show(message, { severity })
      if (mode === 'silent') hasNotifiedSilentlyRef.current = true
    },
    [show]
  )

  const fetchPosition = useCallback(
    async (mode: 'silent' | 'explicit'): Promise<Position | null> => {
      setIsPending(true)

      try {
        let position: Position
        try {
          position = await Geolocation.getCurrentPosition(GEOLOCATION_OPTIONS)
        } catch (error) {
          if (isLocationDisabledError(error) || isPermissionDeniedError(error)) throw error

          logger.warn('getCurrentPosition failed, retrying once', error)
          await new Promise((resolve) => setTimeout(resolve, LOCATION_RETRY_DELAY_MS))
          position = await Geolocation.getCurrentPosition(RETRY_OPTIONS)
        }

        setCurrentLocation(position)
        return position
      } catch (error) {
        logger.error('Failed to get current position', error)

        if (isLocationDisabledError(error)) {
          emitNotification(mode, 'Ative a localização do dispositivo para usar este recurso.', 'warning')
        } else if (isPermissionDeniedError(error)) {
          emitNotification(mode, PERMANENT_DENIED_MSG, 'warning')
        } else {
          emitNotification(mode, 'Não foi possível obter a localização atual.', 'error')
        }
        return null
      } finally {
        setIsPending(false)
      }
    },
    [emitNotification]
  )

  const acquireLocation = useCallback(
    async (mode: 'silent' | 'explicit'): Promise<Position | null> => {
      let status
      try {
        status = await Geolocation.checkPermissions()
        logger.info('Checked location permission', {
          location: status.location,
          coarse: status.coarseLocation,
          mode,
        })
      } catch (error) {
        logger.warn('checkPermissions failed (location services likely off)', error)
        if (mode === 'silent') {
          if (hasSilentlyPromptedRef.current) return null
          hasSilentlyPromptedRef.current = true
        }
        return fetchPosition(mode)
      }

      if (hasLocationAccess(status)) {
        return fetchPosition(mode)
      }

      if (isLocationDenied(status)) {
        emitNotification(mode, PERMANENT_DENIED_MSG, 'warning')
        return null
      }

      if (mode === 'silent') {
        if (hasSilentlyPromptedRef.current) return null
        if (!canPromptLocation(status)) return null
        hasSilentlyPromptedRef.current = true
      }

      if (!Capacitor.isNativePlatform()) {
        return fetchPosition(mode)
      }

      try {
        const requested = await Geolocation.requestPermissions()
        logger.info('Requested location permission', {
          location: requested.location,
          coarse: requested.coarseLocation,
          mode,
        })

        if (hasLocationAccess(requested)) {
          return fetchPosition(mode)
        }

        if (isLocationDenied(requested)) {
          emitNotification(mode, PERMANENT_DENIED_MSG, 'warning')
        }

        return null
      } catch (error) {
        logger.warn('requestPermissions failed (location services likely off)', error)
        return fetchPosition(mode)
      }
    },
    [fetchPosition, emitNotification]
  )

  const ensureLocation = useCallback(async (): Promise<Position | null> => {
    if (currentLocation) return currentLocation
    return acquireLocation('silent')
  }, [currentLocation, acquireLocation])

  const triggerRequestLocation = useCallback(
    (): Promise<Position | null> => acquireLocation('explicit'),
    [acquireLocation]
  )

  const coords = useMemo(() => (currentLocation ? positionToCoords(currentLocation) : null), [currentLocation])

  return (
    <GeolocationContext.Provider value={{ currentLocation, coords, isPending, ensureLocation, triggerRequestLocation }}>
      {children}
    </GeolocationContext.Provider>
  )
}

export const useGeolocationContext = () => {
  const context = useContext(GeolocationContext)

  if (!context) {
    throw new Error('useGeolocationContext must be used within a GeolocationProvider')
  }

  return context
}
